import toast from "@components/Toast";
import UserVerificationCode from "@components/UserVerificationCode";
import { TextButton } from "@components/library";
import { COLORS, FEATURE_FLAGS } from "@constants";
import { useSearchParams } from "@hooks/router";
import haloLogo from "@images/logos/halo_logo.svg";
import { fetchSubscription } from "@redux/actions/companyActions";
import { setCurrentUser } from "@redux/actions/profileActions";
import { RootState } from "@redux/store";
import { UserNotVerifiedError, cancelSignUp, onboard } from "@requests/users";
import { discover_path, proposal_review_system_path } from "@routes/routes";
import { useMutation } from "@tanstack/react-query";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { UserRole } from "@tsTypes/users";
import { identify, track } from "@utils/appUtils";
import { t } from "@utils/i18n";
import { toastErrorHandler } from "@utils/requests";
import {
  clearSignupPrefillData,
  getSignupPrefillData,
  mapUdtypeToUserInfo,
} from "@utils/signupPrefill";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { Progress } from "./Progress";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";

export enum UserDetailedType {
  // Scientist user types
  UNIVERSITY_SCIENTIST = "university scientist",
  STARTUP_SCIENTIST = "startup scientist",
  SUPPLIER = "supplier",
  CONSULTANT = "consultant",
  // Sponsor user types
  COMPANY_SPONSOR = "company sponsor",
  VENTURE_CAPITAL_SPONSOR = "venture capital sponsor", // Deprecated
  GOVERNMENT_SPONSOR = "government sponsor", // Deprecated
  // Admin user types
  UNIVERSITY_ADMINISTRATOR = "university administrator",
}

export interface UserInfo {
  role?: "company-sponsor" | "research-scientist" | "company-scientist" | "research-administrator";
  detailedType?: UserDetailedType;
}

export type BaseOnboardingProfileInfo = Partial<{
  university: string | undefined;
  howDidYouHearAboutUs: string | undefined;
  howDidYouHearAboutUsOther: string;
}>;

function OnboardingContainer() {
  // Get prefill data from URL params/localStorage
  const prefillData = getSignupPrefillData();
  const initialUserInfo = mapUdtypeToUserInfo(prefillData.udtype);

  const [profileInfo, setProfileInfo] = useState<any>({});
  const [completionState, setCompletionState] = useState({
    step1: false,
    step2: false,
    step3: false,
  });
  const [userInfo, setUserInfo] = useState<UserInfo>(initialUserInfo);

  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);

  const dispatch = useDispatch();
  const history = useHistory();

  const [searchParams] = useSearchParams();
  const commenterInviteRequest = searchParams.get("commenter_invite_request");

  useEffect(() => {
    if (currentUser.profile_info?.id) {
      history.push("/");
    }
    if (currentUser.verified) {
      onContinue("step1");
    }
  }, []);

  useEffect(() => {
    if (completionState.step1 && commenterInviteRequest) {
      setUserInfo({
        ...userInfo,
        role: "company-sponsor",
        detailedType: UserDetailedType.COMPANY_SPONSOR,
      });
      setCompletionState({ ...completionState, step1: true, step2: true });
    }
  }, [completionState.step1]);

  const scrollToRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    scrollToRef.current?.scrollIntoView({ behavior: "smooth" });
    track(SegmentEventName.VIEW, {
      react_component: "OnboardingContainer",
      completionState,
    });
  }, [completionState]);

  const onboardMutation = useMutation({
    mutationFn: (userType: "scientist" | "sponsor" | "admin") =>
      onboard({
        userType,
        profileInfo,
        detailedType: userInfo.detailedType,
        commenterInviteRequest,
      }),
    onError: (error, variables, context): void => {
      if (error instanceof UserNotVerifiedError) {
        setCompletionState({
          step1: false,
          step2: false,
          step3: false,
        });
        toast.error(t("pages.onboarding_container.user_not_verified_message"));
        return;
      }
      toastErrorHandler(error, variables, context);
    },
    onSuccess: (updatedUser: any) => {
      // Clear prefill data after successful onboarding
      clearSignupPrefillData();

      dispatch(setCurrentUser(updatedUser));

      identify(updatedUser);

      if (updatedUser.role === UserRole.SPONSOR) {
        dispatch(fetchSubscription(updatedUser.profile_info.company_identifier));

        if (commenterInviteRequest) {
          history.push(proposal_review_system_path({ request: commenterInviteRequest }));
        }

        /**
         * After onboarding, if Discover is enable we immediately go to the
         * discover page.
         */
        if (FEATURE_FLAGS.DISCOVER_FEED) {
          history.push(discover_path());
        }
      }

      onContinue("step3");
    },
  });

  const cancelSignUpMutation = useMutation({
    mutationFn: () => cancelSignUp(completionState.toString()),
    onSuccess: () => {
      // Take user back to landing page after canceling sign up
      window.location.href = "/";
    },
    onError: toastErrorHandler,
  });

  const handleOnboard = (userType: "scientist" | "sponsor" | "admin") => {
    if (onboardMutation.isLoading) {
      return;
    }
    onboardMutation.mutate(userType);
  };

  const onVerify = () => {
    dispatch(
      setCurrentUser({
        ...currentUser,
        verified: true,
      })
    );
    onContinue("step1");
  };

  const updateValue = (key: "role" | "detailedType", newValue) => {
    if (newValue !== userInfo[key]) {
      setProfileInfo({});
    }

    setUserInfo((prevUserInfo) => ({
      ...prevUserInfo,
      [key]: newValue,
    }));
  };

  // when the continue button is pressed, set the current step to complete
  const onContinue = (currentStep) => {
    setCompletionState({ ...completionState, [currentStep]: true });
  };

  const currentStep = () => {
    const { step1, step2, step3 } = completionState;
    if (!step1) {
      return <UserVerificationCode onContinue={onVerify} />;
    } else if (!step2) {
      return (
        <Step2
          values={userInfo}
          setRole={(v) => updateValue("role", v)}
          setDetailedType={(v) => updateValue("detailedType", v)}
          onContinue={() => onContinue("step2")}
        />
      );
    } else if (!step3) {
      return (
        <Step3
          userType={userInfo.role}
          handleOnboard={handleOnboard}
          isSubmitting={onboardMutation.isLoading}
          onBack={() => {
            setCompletionState({
              ...completionState,
              step2: false,
            });
          }}
          profileInfo={profileInfo}
          setProfileInfo={setProfileInfo}
          user={currentUser}
        />
      );
    }
    return <Step4 userType={userInfo.role} />;
  };

  const allStepsComplete = Object.values(completionState).every((step) => step);

  return (
    <OnboardingPage>
      <Banner>
        <img src={haloLogo} />
        {!allStepsComplete && (
          <TextButton
            text={t("pages.onboarding_container.cancel_sign_up")}
            color={COLORS.HALO_BLUE}
            iconName={null}
            onClick={() => {
              track(SegmentEventName.CLICK, {
                react_component: "OnboardingContainer",
                ui_component: "Cancel sign up button",
              });
              cancelSignUpMutation.mutate({});
            }}
            disabled={cancelSignUpMutation.isLoading}
          />
        )}
      </Banner>
      <Progress completionState={completionState} />
      <StepContainer>
        <div ref={scrollToRef} />
        {currentStep()}
      </StepContainer>
    </OnboardingPage>
  );
}

export default OnboardingContainer;

const OnboardingPage = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
  width: 100%;
  align-items: center;
`;

const Banner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  text-align: center;
  background-color: ${COLORS.WHITE};
  height: 84px;
  margin-bottom: 48px;
  width: 100vw;
  padding: 26px 36px;
  border-bottom: 1px solid ${COLORS.NEUTRAL_200};
`;
const StepContainer = styled.div`
  padding-bottom: 40px;
`;
